import {
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    Stack,
    Typography,
    alpha,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import PaymentsIcon from '@mui/icons-material/Payments';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';

const STRIPE_COLOR = '#635BFF';

function StripeLogo({ size = 32 }: { size?: number }) {
    return (
        <Box sx={{
            width: size, height: size, borderRadius: 1,
            bgcolor: STRIPE_COLOR, display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
            <svg width={size * 0.6} height={size * 0.6} viewBox="0 0 24 24" fill="white">
                <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z" />
            </svg>
        </Box>
    );
}

type StripeConnectedViewProps = {
    publishableKey?: string;
    lastSync?: { at: string; summary: string } | null;
    syncText?: string | null;
    syncing?: boolean;
    onSync?: () => void;
    onUpdateKeys?: () => void;
    onDisconnect?: () => void;
    canSync?: boolean;
    canConnect?: boolean;
};

type StripeConnectViewProps = {
    publishableKey: string;
    secretKey: string;
    onPkChange: (v: string) => void;
    onSkChange: (v: string) => void;
    onSave: () => void;
    onCancel?: () => void;
    saving?: boolean;
    isUpdate?: boolean;
    canConnect?: boolean;
};

export function StripeConnectView({
    publishableKey, secretKey, onPkChange, onSkChange, onSave, onCancel, saving, isUpdate, canConnect = true,
}: StripeConnectViewProps) {
    return (
        <Stack spacing={3}>
            <Stack direction="row" spacing={1.5} alignItems="center">
                <StripeLogo size={36} />
                <Box>
                    <Typography variant="subtitle1" fontWeight={500}>
                        {isUpdate ? 'Update Stripe keys' : 'Connect your Stripe account'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {isUpdate ? 'Enter new API keys to replace the current ones' : 'Accept card payments directly from invoice links'}
                    </Typography>
                </Box>
            </Stack>
            <Divider />
            {!isUpdate && (
                <Box>
                    <Typography variant="overline" color="text.secondary" display="block" gutterBottom>
                        What this enables
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                        {[
                            { icon: <PaymentsIcon fontSize="small" />, title: 'Online payments', description: 'Clients can pay invoices via card directly from the shared document link' },
                            { icon: <SyncIcon fontSize="small" />, title: 'Transaction sync', description: 'Pull Stripe payments into your transaction feed on demand' },
                        ].map((item) => (
                            <Card variant="outlined" key={item.title} sx={{ height: '100%' }}>
                                <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                                    <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                                        <Box sx={{ color: STRIPE_COLOR }}>{item.icon}</Box>
                                        <Typography variant="body2" fontWeight={500}>{item.title}</Typography>
                                    </Stack>
                                    <Typography variant="caption" color="text.secondary">{item.description}</Typography>
                                </CardContent>
                            </Card>
                        ))}
                    </Box>
                </Box>
            )}
            <Stack spacing={1.5}>
                <Box>
                    <Typography variant="caption" fontWeight={500} display="block" mb={0.5}>Publishable Key</Typography>
                    <input
                        type="text"
                        placeholder="pk_live_..."
                        value={publishableKey}
                        onChange={e => onPkChange(e.target.value)}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, fontFamily: 'monospace', boxSizing: 'border-box' }}
                    />
                </Box>
                <Box>
                    <Typography variant="caption" fontWeight={500} display="block" mb={0.5}>Secret Key</Typography>
                    <input
                        type="password"
                        placeholder="sk_live_..."
                        value={secretKey}
                        onChange={e => onSkChange(e.target.value)}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, fontFamily: 'monospace', boxSizing: 'border-box' }}
                    />
                </Box>
            </Stack>
            <Box sx={{
                display: 'flex', gap: 1, alignItems: 'flex-start',
                bgcolor: (t) => alpha(t.palette.success.main, 0.06),
                border: '1px solid', borderColor: (t) => alpha(t.palette.success.main, 0.2),
                borderRadius: 2, px: 2, py: 1.5,
            }}>
                <ShieldOutlinedIcon sx={{ color: 'success.main', fontSize: 18, mt: 0.2, flexShrink: 0 }} />
                <Typography variant="caption" color="success.dark" lineHeight={1.6}>
                    Your secret key is stored encrypted and never exposed to clients. Only the publishable key is sent to the browser.
                </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
                <Button
                    variant="contained"
                    onClick={onSave}
                    disabled={saving || !publishableKey.trim() || !secretKey.trim() || !canConnect}
                    sx={{
                        bgcolor: STRIPE_COLOR, '&:hover': { bgcolor: '#4f46e5' },
                        textTransform: 'none', fontWeight: 500, borderRadius: 1, color: '#fff', flex: 1,
                    }}
                >
                    {saving ? 'Saving…' : isUpdate ? 'Update keys' : 'Connect Stripe'}
                </Button>
                {onCancel && (
                    <Button
                        variant="outlined"
                        onClick={onCancel}
                        sx={{ textTransform: 'none', borderRadius: 1 }}
                    >
                        Cancel
                    </Button>
                )}
            </Stack>
        </Stack>
    );
}

export function StripeConnectedView({
    publishableKey,
    lastSync,
    syncText,
    syncing,
    onSync,
    onUpdateKeys,
    onDisconnect,
    canSync = true,
    canConnect = true,
}: StripeConnectedViewProps) {
    const fmtDate = (iso?: string | null) =>
        iso ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso)) : '—';

    const maskedKey = publishableKey
        ? `${publishableKey.slice(0, 8)}${'•'.repeat(12)}${publishableKey.slice(-4)}`
        : '—';

    return (
        <Stack spacing={2.5}>
            <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <StripeLogo size={36} />
                    <Box>
                        <Typography variant="subtitle1" fontWeight={500}>Stripe Payments</Typography>
                        <Typography sx={{ fontSize: 11, fontStyle: 'italic', color: STRIPE_COLOR }}>Connected</Typography>
                    </Box>
                </Stack>
            </Stack>

            <Divider sx={{ my: 0.5 }} />

            {/* Key + last sync cards */}
            <Box>
                <Typography variant="overline" color="text.secondary" display="block" gutterBottom>
                    Account details
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                    <Card variant="outlined">
                        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Publishable key</Typography>
                            <Typography variant="body2" fontWeight={500} sx={{ fontFamily: 'monospace', fontSize: 12 }}>{maskedKey}</Typography>
                        </CardContent>
                    </Card>
                    <Card variant="outlined">
                        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                            <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                                <SyncIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">Last sync</Typography>
                            </Stack>
                            <Typography variant="body2" fontWeight={500}>{fmtDate(lastSync?.at)}</Typography>
                            {lastSync?.summary && (
                                <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
                                    {lastSync.summary}
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Box>
            </Box>

            {/* Sync */}
            <Box>
                <Typography variant="overline" color="text.secondary" display="block" gutterBottom>
                    Sync data from Stripe
                </Typography>
                <Button
                    variant="outlined"
                    startIcon={<SyncIcon />}
                    onClick={onSync}
                    disabled={syncing || !canSync}
                    sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                    {syncing ? 'Syncing transactions…' : 'Sync transactions'}
                </Button>
            </Box>

            {syncText && syncing && (
                <Box sx={{
                    display: 'flex', gap: 1, alignItems: 'center',
                    bgcolor: (t) => alpha(t.palette.info.main, 0.06),
                    border: '1px solid', borderColor: (t) => alpha(t.palette.info.main, 0.2),
                    borderRadius: 2, px: 2, py: 1.25,
                }}>
                    <SyncIcon sx={{ fontSize: 14, color: 'info.main', flexShrink: 0 }} />
                    <Typography variant="caption" color="info.dark">{syncText}</Typography>
                </Box>
            )}

            <Divider />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button
                    variant="outlined"
                    onClick={onUpdateKeys}
                    disabled={!canConnect}
                    sx={{ textTransform: 'none', borderRadius: 2, flex: 1 }}
                >
                    Update keys
                </Button>
                <Button
                    variant="outlined"
                    color="error"
                    onClick={onDisconnect}
                    disabled={!canConnect}
                    sx={{ textTransform: 'none', borderRadius: 2, flex: 1 }}
                >
                    Disconnect
                </Button>
            </Stack>
        </Stack>
    );
}
